import React, {
  Children,
  forwardRef,
  Fragment,
  isValidElement,
  PropsWithChildren,
  ReactNode,
} from "react";
import Box from "../../layout/Box/Box.web";
import Flex, { type FlexProps } from "../../layout/Flex/Flex.web";

import type { CardSharedProps } from "./Card.props";

const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { children, ...props },
  ref
) {
  const [header, body] = extractSlots(children);

  return (
    <Box
      ref={ref}
      bg="bg"
      rounded={{ sm: "md" }}
      p={{ base: "24px 20px", sm: "xl" }}
      {...props}
    >
      <>
        {header}
        {body}
      </>
    </Box>
  );
}) as CardComponent;

function CardHeader({ children, ...props }: CardProps): JSX.Element {
  return (
    <Box borderTopRadius="md" {...props}>
      {children}
    </Box>
  );
}

/**
 * Snugs up a footer to the bottom of a card with a border on top.
 * Perfect for attaching buttons for actions. Follows a similar
 * style to our `Modal.Footer` component.
 */
function CardFooter(props: FlexProps): JSX.Element {
  return (
    <Flex
      py="2.5"
      px="md"
      mt="md"
      mx="-4"
      mb="-4"
      borderTop="1px solid"
      borderTopColor="border"
      roundedBottom={{ sm: "md" }}
      flexDirection="row-reverse"
      justifyContent="space-between"
      {...props}
    />
  );
}

Card.Header = CardHeader;
Card.Footer = CardFooter;

export type CardProps = CardSharedProps & {
  as?: React.ElementType;
};

type CardComponent = React.ForwardRefExoticComponent<
  CardProps & React.RefAttributes<HTMLDivElement>
> & {
  Header: typeof CardHeader;
  Footer: typeof CardFooter;
};

function extractSlots(
  children: CardProps["children"]
): [ReactNode | undefined, ReactNode] {
  let header: ReactNode | undefined;
  const body: Array<ReactNode> = [];

  Children.forEach(children, (child: ReactNode) => {
    if (!isValidElement(child)) {
      body.push(child);
    } else if (child.type === Fragment) {
      const { children } = child.props as PropsWithChildren<
        Record<string, unknown>
      >;

      if (children) {
        const [headerFragment, bodyFragment] = extractSlots(children);

        header = headerFragment;
        body.push(bodyFragment);
      }
    } else if (child.type === CardHeader) {
      header = child;
    } else {
      body.push(child);
    }
  });

  return [header, body];
}

export default Card;
